import { Sleep_Tag_Enum_Enum, User_Program_Session_Enum_Enum } from '../remote-graphql-types';

export const CognitiveTrainingMinDurationInDays = 5;
export const RelaxingTrainingMinDurationInDays = 7;
export const SleepRestrictionMinDurationInDays = 7;
export const SleepRestrictionMinSleepDiaryToStartQuantity = 2;
export const SleepRestrictionMinSleepDiaryQuantity = 2;
export const GamificationMinPoints = 25;
export const GamificationTargetPoints = 100;

export enum YesNo {
  Yes = 'Sim',
  No = 'Não',
}

export const mapBoolToYesNo = (bool: boolean) => {
  if (bool === true) return YesNo.Yes;
  if (bool === false) return YesNo.No;
  return null;
};

export const mapYesNoToBool = (yesNo: YesNo) => {
  if (yesNo === YesNo.Yes) return true;
  if (yesNo === YesNo.No) return false;
  return null;
};

export const getYesNoLabel = (value: YesNo, t?: (key: string) => string): string => {
  if (!t) {
    return value; // fallback to original Portuguese values
  }
  return value === YesNo.Yes ? t('button_yes') : t('button_no');
};

export enum Gender {
  Female = 'female',
  Male = 'male',
  Other = 'other',
}
/**
 * Returns i18n key for gender
 * @param gender - Gender enum
 * @returns i18n key like 'common:genders.male'
 */
export const getGenderI18nKey = (gender: Gender): string => {
  switch (gender) {
    case Gender.Male:
      return 'common:genders.male';
    case Gender.Female:
      return 'common:genders.female';
    case Gender.Other:
    default:
      return 'common:genders.other';
  }
};

/**
 * Returns neutral gender suffix for Portuguese language
 * For English, this function returns empty string as English doesn't use gender suffixes
 * @param gender - Gender enum
 * @param language - Current language (pt-BR, en, es)
 * @returns Gender suffix ('a', '', '(a)' for Portuguese; '' for English/Spanish)
 */
export const getNeutralGenderSuffix = (gender: Gender | string, language = 'pt-BR'): string => {
  // English and other languages don't use gender suffixes
  if (language === 'en') return '';

  // Portuguese/Spanish use gender suffixes
  return gender === Gender.Male ? '' : gender === Gender.Female ? 'a' : '(a)';
};

/**
 * Returns gender suffix for Portuguese language
 * For English, this function returns empty string as English doesn't use gender suffixes
 * @param gender - Gender enum
 * @param language - Current language (pt-BR, en, es)
 * @returns Gender suffix ('o', 'a', 'o(a)' for Portuguese; '' for English/Spanish)
 */
export const getGenderSuffix = (gender: Gender | string, language = 'pt-BR'): string => {
  // English doesn't use gender suffixes
  if (language === 'en') return '';

  // Portuguese/Spanish use gender suffixes
  return gender === Gender.Male ? 'o' : gender === Gender.Female ? 'a' : 'o(a)';
};

/**
 * Returns gender pronoun for Portuguese language
 * For English, returns he/she/they
 * @param gender - Gender enum
 * @param language - Current language (pt-BR, en, es)
 * @returns Gender pronoun
 */
export const getGenderPronoun = (gender: Gender | string, language = 'pt-BR'): string => {
  if (language === 'en') {
    return gender === Gender.Male ? 'he' : gender === Gender.Female ? 'she' : 'they';
  }

  if (language === 'es') {
    return gender === Gender.Male ? 'él' : gender === Gender.Female ? 'ella' : 'elle';
  }

  // Portuguese (pt-BR)
  return gender === Gender.Male ? 'ele' : gender === Gender.Female ? 'ela' : 'ele(a)';
};

export const getGenderName = (gender: Gender, t?: (key: string) => string): string => {
  if (!t) {
    // Fallback to Portuguese if no translation function provided (backward compatibility)
    switch (gender) {
      case Gender.Male:
        return 'Masculino';
      case Gender.Female:
        return 'Feminino';
      case Gender.Other:
      default:
        return 'Outro';
    }
  }

  switch (gender) {
    case Gender.Male:
      return t('gender_male');
    case Gender.Female:
      return t('gender_female');
    case Gender.Other:
    default:
      return t('gender_other');
  }
};

export enum SessionState {
  NotStarted = 0,
  /**
   * the user has seen the first message of the session but haven't interacted with it
   */
  Seen = 1,
  Finished = 2,
  Started = 3,
}

export const HasuraSessionState: Record<User_Program_Session_Enum_Enum, SessionState> = {
  NOT_STARTED: 0,
  SEEN: 1,
  FINISHED: 2,
  STARTED: 3,
};

// BEGIN: move to packages/global.utils/domain/sleep-factor.ts (TODO: REFACTOR)
/**
 * BUSINESS_RULE: This enum comes from Hasura
 */
export enum SleepDiaryTag {
  Alcohol = 'alcohol',
  Bathroom = 'bathroom',
  Caffeine = 'caffeine',
  Exercise = 'exercise',
  Light = 'light',
  Meal = 'meal',
  Medicine = 'medicine',
  Nicotine = 'nicotine',
  Nap = 'nap',
  Noise = 'noise',
  Pain = 'pain',
  Partner = 'partner',
  Temperature = 'temperature',
  Dream = 'dream',
}

export const getEmojiForSleepDiaryTag = (tag: SleepDiaryTag): string => {
  switch (tag) {
    case SleepDiaryTag.Alcohol:
      return '🍻';
    case SleepDiaryTag.Bathroom:
      return '🚽';
    case SleepDiaryTag.Caffeine:
      return '☕';
    case SleepDiaryTag.Exercise:
      return '🏃';
    case SleepDiaryTag.Light:
      return '💡';
    case SleepDiaryTag.Meal:
      return '🍔';
    case SleepDiaryTag.Medicine:
      return '💊';
    case SleepDiaryTag.Nicotine:
      return '🚬';
    case SleepDiaryTag.Nap:
      return '💤';
    case SleepDiaryTag.Noise:
      return '🔊';
    case SleepDiaryTag.Pain:
      return '🤕';
    case SleepDiaryTag.Partner:
      return '🛌';
    case SleepDiaryTag.Temperature:
      return '❄️☀️';
    case SleepDiaryTag.Dream:
      return '☁️';
    default:
      throw new Error(`invalid tag: ${tag}`);
  }
};
/**
 * Returns i18n key for sleep diary tag
 * @param tag - Sleep diary tag enum
 * @returns i18n key like 'common:sleepTags.alcohol'
 */
export const getSleepTagI18nKey = (tag: SleepDiaryTag): string => {
  switch (tag) {
    case SleepDiaryTag.Alcohol:
      return 'common:sleepTags.alcohol';
    case SleepDiaryTag.Bathroom:
      return 'common:sleepTags.bathroom';
    case SleepDiaryTag.Caffeine:
      return 'common:sleepTags.caffeine';
    case SleepDiaryTag.Exercise:
      return 'common:sleepTags.exercise';
    case SleepDiaryTag.Light:
      return 'common:sleepTags.light';
    case SleepDiaryTag.Meal:
      return 'common:sleepTags.meal';
    case SleepDiaryTag.Medicine:
      return 'common:sleepTags.medicine';
    case SleepDiaryTag.Nicotine:
      return 'common:sleepTags.nicotine';
    case SleepDiaryTag.Nap:
      return 'common:sleepTags.nap';
    case SleepDiaryTag.Noise:
      return 'common:sleepTags.noise';
    case SleepDiaryTag.Pain:
      return 'common:sleepTags.pain';
    case SleepDiaryTag.Partner:
      return 'common:sleepTags.partner';
    case SleepDiaryTag.Temperature:
      return 'common:sleepTags.temperature';
    case SleepDiaryTag.Dream:
      return 'common:sleepTags.dream';
    default:
      throw new Error(`invalid tag: ${tag}`);
  }
};

export const getNameForSleepDiaryTag = (tag: SleepDiaryTag, t?: (key: string) => string): string => {
  if (!t) {
    // Fallback to Portuguese if no translation function provided (backward compatibility)
    switch (tag) {
      case SleepDiaryTag.Alcohol:
        return 'Álcool';
      case SleepDiaryTag.Bathroom:
        return 'Acordou para ir ao banheiro';
      case SleepDiaryTag.Caffeine:
        return 'Cafeína';
      case SleepDiaryTag.Exercise:
        return 'Exercício';
      case SleepDiaryTag.Light:
        return 'Luz/Claridade';
      case SleepDiaryTag.Meal:
        return 'Refeição pesada';
      case SleepDiaryTag.Medicine:
        return 'Remédio';
      case SleepDiaryTag.Nicotine:
        return 'Nicotina';
      case SleepDiaryTag.Nap:
        return 'Cochilo';
      case SleepDiaryTag.Noise:
        return 'Barulho';
      case SleepDiaryTag.Pain:
        return 'Dor';
      case SleepDiaryTag.Partner:
        return 'Parceiro na cama';
      case SleepDiaryTag.Temperature:
        return 'Temperatura';
      case SleepDiaryTag.Dream:
        return 'Sonho/Pesadelo';
      default:
        throw new Error(`invalid tag: ${tag}`);
    }
  }

  switch (tag) {
    case SleepDiaryTag.Alcohol:
      return t('sleep_tag_alcohol');
    case SleepDiaryTag.Bathroom:
      return t('sleep_tag_bathroom');
    case SleepDiaryTag.Caffeine:
      return t('sleep_tag_caffeine');
    case SleepDiaryTag.Exercise:
      return t('sleep_tag_exercise');
    case SleepDiaryTag.Light:
      return t('sleep_tag_light');
    case SleepDiaryTag.Meal:
      return t('sleep_tag_meal');
    case SleepDiaryTag.Medicine:
      return t('sleep_tag_medicine');
    case SleepDiaryTag.Nicotine:
      return t('sleep_tag_nicotine');
    case SleepDiaryTag.Nap:
      return t('sleep_tag_nap');
    case SleepDiaryTag.Noise:
      return t('sleep_tag_noise');
    case SleepDiaryTag.Pain:
      return t('sleep_tag_pain');
    case SleepDiaryTag.Partner:
      return t('sleep_tag_partner');
    case SleepDiaryTag.Temperature:
      return t('sleep_tag_temperature');
    case SleepDiaryTag.Dream:
      return t('sleep_tag_dream');
    default:
      throw new Error(`invalid tag: ${tag}`);
  }
};
export const getEmojiAndNameForSleepDiaryTag = (tag: SleepDiaryTag, t?: (key: string) => string): string =>
  `${getEmojiForSleepDiaryTag(tag)} ${getNameForSleepDiaryTag(tag, t)}`;
// END: move to packages/global.utils/domain/sleep-factor.ts

export enum SleepDiaryGrade {
  Good = 3,
  Ok = 2,
  Bad = 1,
}
export const getEmojiForSleepDiaryGrade = (grade: SleepDiaryGrade): string => {
  switch (Math.round(grade)) {
    case SleepDiaryGrade.Good:
      return '🙂';
    case SleepDiaryGrade.Ok:
      return '😐';
    case SleepDiaryGrade.Bad:
      return '😞';
    default:
      console.error('ERROR: getEmojiForSleepDiaryGrade -> invalid grade', grade);
      return '';
  }
};
/**
 * Returns i18n key for sleep diary grade
 * @param grade - Sleep diary grade enum
 * @returns i18n key like 'common:sleepGrades.rested'
 */
export const getSleepGradeI18nKey = (grade: SleepDiaryGrade): string => {
  switch (Math.round(grade)) {
    case SleepDiaryGrade.Good:
      return 'common:sleepGrades.rested';
    case SleepDiaryGrade.Ok:
      return 'common:sleepGrades.somewhatRested';
    case SleepDiaryGrade.Bad:
      return 'common:sleepGrades.tired';
    default:
      console.error('ERROR: getSleepGradeI18nKey -> invalid grade', grade);
      return '';
  }
};

export const getTextForSleepDiaryGrade = (grade: SleepDiaryGrade): string => {
  switch (Math.round(grade)) {
    case SleepDiaryGrade.Good:
      return 'Descansado';
    case SleepDiaryGrade.Ok:
      return 'Mais ou menos descansado';
    case SleepDiaryGrade.Bad:
      return 'Cansado';
    default:
      console.error('ERROR: getEmojiForSleepDiaryGrade -> invalid grade', grade);
      return '';
  }
};
/**
 * Round between 0 and 100
 * @param value between 1 and 3
 */
export const roundGradeValue = (value: number): number => value && Math.round(((value - 1) * 100) / 2);

/**
 * BUSINESS_RULE: This enum values comes from Hasura
 * This is defined on the server, front-end and chatfuel
 * So, DON'T CHANGE IT
 */
export enum SleepDiaryTechnique {
  DeepBreath = 'deep_breath',
  Meditation = 'meditation',
  PMR = 'pmr',
  Imagery = 'imagery',
  AutgenicTraining = 'autogenic_training',

  BehaviorActivation = 'behavior_activation',
  ParkingLot = 'parking_lot',
  ParadoxicalIntention = 'paradoxical_intention',
  ThoughtBlock = 'thought_block',
  Gratitude = 'gratitude',
  ChallengeCatastrophicThinking = 'challenge_catastrophic_thinking',

  LightTherapy = 'light_therapy',

  StimulusControl = 'stimulus_control',
  SleepRestriction = 'sleep_restriction',
}
// Map technique enum to i18n key
export const getTechniqueI18nKey = (technique: SleepDiaryTechnique): string => {
  const techniqueDict: Record<SleepDiaryTechnique, string> = {
    [SleepDiaryTechnique.ThoughtBlock]: 'common:techniques.thoughtBlocking',
    [SleepDiaryTechnique.StimulusControl]: 'common:techniques.stimulusControl',
    [SleepDiaryTechnique.ParkingLot]: 'common:techniques.parkingLot',
    [SleepDiaryTechnique.LightTherapy]: 'common:techniques.phototherapy',
    [SleepDiaryTechnique.Gratitude]: 'common:techniques.gratitude',
    [SleepDiaryTechnique.ParadoxicalIntention]: 'common:techniques.paradoxicalIntention',
    [SleepDiaryTechnique.Meditation]: 'common:techniques.mindfulness',
    [SleepDiaryTechnique.PMR]: 'common:techniques.progressiveMuscleRelaxation',
    [SleepDiaryTechnique.DeepBreath]: 'common:techniques.deepBreathing',
    [SleepDiaryTechnique.AutgenicTraining]: 'common:techniques.autogenicTraining',
    [SleepDiaryTechnique.ChallengeCatastrophicThinking]: 'common:techniques.challengeNegativeThoughts',
    [SleepDiaryTechnique.SleepRestriction]: 'common:techniques.sleepRestriction',
    [SleepDiaryTechnique.Imagery]: 'common:techniques.visualization',
    [SleepDiaryTechnique.BehaviorActivation]: 'common:techniques.behavioralActivation',
  };
  return techniqueDict[technique];
};

export const getNameForSleepDiaryTechnique = (technique: SleepDiaryTechnique, t?: (key: string) => string): string => {
  if (!t) {
    // Fallback to Portuguese if no translation function provided (backward compatibility)
    const techniqueDict: Record<SleepDiaryTechnique, string> = {
      [SleepDiaryTechnique.ThoughtBlock]: `Bloqueio de pensamento`,
      [SleepDiaryTechnique.StimulusControl]: `Controle de estímulos`,
      [SleepDiaryTechnique.ParkingLot]: `Estacionamento de ideias`,
      [SleepDiaryTechnique.LightTherapy]: `Fototerapia`,
      [SleepDiaryTechnique.Gratitude]: `Gratidão`,
      [SleepDiaryTechnique.ParadoxicalIntention]: `Intenção paradoxal`,
      [SleepDiaryTechnique.Meditation]: `Meditação mindfulness`,
      [SleepDiaryTechnique.PMR]: `Relaxamento muscular progressivo`,
      [SleepDiaryTechnique.DeepBreath]: `Respiração profunda`,
      [SleepDiaryTechnique.AutgenicTraining]: `Treinamento autogeno`,
      [SleepDiaryTechnique.ChallengeCatastrophicThinking]: 'Desafiar pensamentos negativos',
      [SleepDiaryTechnique.SleepRestriction]: `Restrição do sono`,
      [SleepDiaryTechnique.Imagery]: `Visualização`,
      [SleepDiaryTechnique.BehaviorActivation]: 'Ativação de comportamento',
    };
    return techniqueDict[technique];
  }

  const techniqueDict: Record<SleepDiaryTechnique, string> = {
    [SleepDiaryTechnique.ThoughtBlock]: t('technique_thought_block'),
    [SleepDiaryTechnique.StimulusControl]: t('technique_stimulus_control'),
    [SleepDiaryTechnique.ParkingLot]: t('technique_parking_lot'),
    [SleepDiaryTechnique.LightTherapy]: t('technique_light_therapy'),
    [SleepDiaryTechnique.Gratitude]: t('technique_gratitude'),
    [SleepDiaryTechnique.ParadoxicalIntention]: t('technique_paradoxical_intention'),
    [SleepDiaryTechnique.Meditation]: t('technique_meditation'),
    [SleepDiaryTechnique.PMR]: t('technique_pmr'),
    [SleepDiaryTechnique.DeepBreath]: t('technique_deep_breath'),
    [SleepDiaryTechnique.AutgenicTraining]: t('technique_autogenic_training'),
    [SleepDiaryTechnique.ChallengeCatastrophicThinking]: t('technique_challenge_catastrophic_thinking'),
    [SleepDiaryTechnique.SleepRestriction]: t('technique_sleep_restriction'),
    [SleepDiaryTechnique.Imagery]: t('technique_imagery'),
    [SleepDiaryTechnique.BehaviorActivation]: t('technique_behavior_activation'),
  };
  return techniqueDict[technique];
};
export const getEmojiForSleepDiaryTechnique = (technique: SleepDiaryTechnique): string => {
  const techniqueDict: Record<SleepDiaryTechnique, string> = {
    [SleepDiaryTechnique.DeepBreath]: '👃',
    [SleepDiaryTechnique.PMR]: '💪',
    [SleepDiaryTechnique.Meditation]: '🧘‍♀️',
    [SleepDiaryTechnique.AutgenicTraining]: '🌡',
    [SleepDiaryTechnique.Imagery]: '👁',
    [SleepDiaryTechnique.ParkingLot]: '📓',
    [SleepDiaryTechnique.ParadoxicalIntention]: '🤔',
    [SleepDiaryTechnique.ThoughtBlock]: '✋',
    [SleepDiaryTechnique.StimulusControl]: '🐶',
    [SleepDiaryTechnique.ChallengeCatastrophicThinking]: '💭',
    [SleepDiaryTechnique.Gratitude]: '🙏',
    [SleepDiaryTechnique.LightTherapy]: '💡',
    [SleepDiaryTechnique.SleepRestriction]: '👮‍♂️',
    [SleepDiaryTechnique.BehaviorActivation]: '🧲',
  };
  return techniqueDict[technique];
};
export const getEmojiAndNameForSleepDiaryTechnique = (technique: SleepDiaryTechnique, t?: (key: string) => string): string =>
  `${getEmojiForSleepDiaryTechnique(technique)} ${getNameForSleepDiaryTechnique(technique, t)}`;

const SLEEP_DIARY_TECHNIQUE_IMAGES_BASE_URL = 'https://vigilantes-app-images.s3.us-east-1.amazonaws.com/assets/activities';
export const getImageUrlForSleepDiaryTechnique = (technique: SleepDiaryTechnique): string => {
  const techniqueDict: Record<SleepDiaryTechnique, string> = {
    [SleepDiaryTechnique.DeepBreath]: 'respiracao-profunda',
    [SleepDiaryTechnique.PMR]: 'relaxamento-muscular-progressivo',
    [SleepDiaryTechnique.Meditation]: 'meditacao-mindfulness',
    [SleepDiaryTechnique.AutgenicTraining]: 'treinamento-autogeno',
    [SleepDiaryTechnique.Imagery]: 'visualizacao',
    [SleepDiaryTechnique.ParkingLot]: 'estacionamento-de-ideias',
    [SleepDiaryTechnique.ParadoxicalIntention]: 'intencao-paradoxal',
    [SleepDiaryTechnique.ThoughtBlock]: 'bloqueio-de-pensamento',
    [SleepDiaryTechnique.StimulusControl]: 'controle-de-estimulos',
    [SleepDiaryTechnique.ChallengeCatastrophicThinking]: 'desafiar-pensamentos-negativos',
    [SleepDiaryTechnique.Gratitude]: 'gratidao',
    [SleepDiaryTechnique.LightTherapy]: 'fototerapia',
    [SleepDiaryTechnique.SleepRestriction]: 'restricao-do-sono',
    [SleepDiaryTechnique.BehaviorActivation]: 'ativacao-de-comportamento',
  };
  return `${SLEEP_DIARY_TECHNIQUE_IMAGES_BASE_URL}/${techniqueDict[technique]}.jpg`;
};
export enum TechniqueKind {
  Behaviour = 'behaviour',
  Cognitive = 'cognitive',
  Relax = 'relax',
  Other = 'other',
}
// Map technique kind to i18n key
export const getTechniqueKindI18nKey = (kind: TechniqueKind): string => {
  switch (kind) {
    case TechniqueKind.Behaviour:
      return 'common:techniqueCategories.behavioural';
    case TechniqueKind.Cognitive:
      return 'common:techniqueCategories.cognitive';
    case TechniqueKind.Relax:
      return 'common:techniqueCategories.relax';
    case TechniqueKind.Other:
      return 'common:techniqueCategories.other';
    default:
      return kind;
  }
};

export const getNameForTechniqueKind = (kind: TechniqueKind): string => {
  switch (kind) {
    case TechniqueKind.Behaviour:
      return 'Comportamental';
    case TechniqueKind.Cognitive:
      return 'Cognitiva';
    case TechniqueKind.Relax:
      return 'Relaxamento';
    case TechniqueKind.Other:
      return 'Outro';
    default:
      return kind;
  }
};
// These values are also used by Hasura, therefore, DONT CHANGE IT
export enum ResourceKind {
  App = 'app',
  Site = 'site',
  Spotify = 'spotify',
  Youtube = 'youtube',
}

export interface TechniqueMeta {
  kind: TechniqueKind;
  duration: number;
}

export function getTechniqueMeta(technique: SleepDiaryTechnique): TechniqueMeta {
  const techniqueDict: Record<SleepDiaryTechnique, TechniqueMeta> = {
    [SleepDiaryTechnique.DeepBreath]: {
      kind: TechniqueKind.Relax,
      duration: 5,
    },
    [SleepDiaryTechnique.PMR]: {
      kind: TechniqueKind.Relax,
      duration: 10,
    },
    [SleepDiaryTechnique.Meditation]: {
      kind: TechniqueKind.Relax,
      duration: 15,
    },
    [SleepDiaryTechnique.AutgenicTraining]: {
      kind: TechniqueKind.Relax,
      duration: 10,
    },
    [SleepDiaryTechnique.Imagery]: {
      kind: TechniqueKind.Relax,
      duration: 10,
    },
    [SleepDiaryTechnique.ParkingLot]: {
      kind: TechniqueKind.Cognitive,
      duration: 5,
    },
    [SleepDiaryTechnique.ParadoxicalIntention]: {
      kind: TechniqueKind.Cognitive,
      duration: null,
    },
    [SleepDiaryTechnique.ThoughtBlock]: {
      kind: TechniqueKind.Cognitive,
      duration: null,
    },
    [SleepDiaryTechnique.StimulusControl]: {
      kind: TechniqueKind.Behaviour,
      duration: null,
    },
    [SleepDiaryTechnique.Gratitude]: {
      kind: TechniqueKind.Cognitive,
      duration: 5,
    },
    [SleepDiaryTechnique.ChallengeCatastrophicThinking]: {
      kind: TechniqueKind.Cognitive,
      duration: 5,
    },
    [SleepDiaryTechnique.BehaviorActivation]: {
      kind: TechniqueKind.Cognitive,
      duration: null,
    },
    [SleepDiaryTechnique.LightTherapy]: {
      kind: TechniqueKind.Other,
      duration: 20,
    },
    [SleepDiaryTechnique.SleepRestriction]: {
      kind: TechniqueKind.Behaviour,
      duration: null,
    },
  };
  return techniqueDict[technique];
}
// END: move to packages/global.utils/domain/sleep-factor.ts

/**
 * Values come from this form: https://admin.typeform.com/form/c0vuw5/create
 * DON'T CHANGE IT without changing on typeform as well!
 */
export enum Comobidities {
  Anxiety = 'Ansiedade geral',
  Depression = 'Depressão',
  Bipolar = 'Transtorno bipolar',
  Schizophrenia = 'Esquizofrenia',
  Dementia = 'Demência (Alzheimer)',
  Epilepsy = 'Epilepsia',
  Parkinson = 'Mal de Parkinson',
  Diabetes = 'Diabetes',
  Reflux = 'Refluxo',
  Apnea = 'Apneia obstrutiva do sono',
  Musculoskeletal = 'Distúrbios músculo-esqueléticos',
  Nocturia = 'Noctúria',
}

/**
 * Returns i18n key for comorbidity condition
 * Note: The enum VALUES above are used in external forms (Typeform) and should NOT be changed.
 * This function maps those values to i18n keys for display purposes.
 * @param condition - Comobidities enum value
 * @returns i18n key like 'medical:conditions.anxiety'
 */
export const getComorbiditiesI18nKey = (condition: Comobidities): string => {
  switch (condition) {
    case Comobidities.Anxiety:
      return 'medical:conditions.anxiety';
    case Comobidities.Depression:
      return 'medical:conditions.depression';
    case Comobidities.Bipolar:
      return 'medical:conditions.bipolar';
    case Comobidities.Schizophrenia:
      return 'medical:conditions.schizophrenia';
    case Comobidities.Dementia:
      return 'medical:conditions.dementia';
    case Comobidities.Epilepsy:
      return 'medical:conditions.epilepsy';
    case Comobidities.Parkinson:
      return 'medical:conditions.parkinson';
    case Comobidities.Diabetes:
      return 'medical:conditions.diabetes';
    case Comobidities.Reflux:
      return 'medical:conditions.reflux';
    case Comobidities.Apnea:
      return 'medical:conditions.apnea';
    case Comobidities.Musculoskeletal:
      return 'medical:conditions.musculoskeletal';
    case Comobidities.Nocturia:
      return 'medical:conditions.nocturia';
    default:
      throw new Error(`Invalid comorbidity: ${condition}`);
  }
};

export enum TagClass {
  beforeSleep,
  afterSleep,
}

export const mapSleep_Tag_Enum_EnumToTagClass = (tag: Sleep_Tag_Enum_Enum): TagClass => {
  switch (tag) {
    case Sleep_Tag_Enum_Enum.Alcohol:
      return TagClass.beforeSleep;
    case Sleep_Tag_Enum_Enum.Bathroom:
      return TagClass.afterSleep;
    case Sleep_Tag_Enum_Enum.Caffeine:
      return TagClass.beforeSleep;
    case Sleep_Tag_Enum_Enum.Exercise:
      return TagClass.beforeSleep;
    case Sleep_Tag_Enum_Enum.Light:
      return TagClass.afterSleep;
    case Sleep_Tag_Enum_Enum.Meal:
      return TagClass.beforeSleep;
    case Sleep_Tag_Enum_Enum.Medicine:
      return TagClass.beforeSleep;
    case Sleep_Tag_Enum_Enum.Nicotine:
      return TagClass.beforeSleep;
    case Sleep_Tag_Enum_Enum.Nap:
      return TagClass.beforeSleep;
    case Sleep_Tag_Enum_Enum.Noise:
      return TagClass.afterSleep;
    case Sleep_Tag_Enum_Enum.Pain:
      return TagClass.afterSleep;
    case Sleep_Tag_Enum_Enum.Partner:
      return TagClass.afterSleep;
    case Sleep_Tag_Enum_Enum.Temperature:
      return TagClass.afterSleep;
    case Sleep_Tag_Enum_Enum.Dream:
      return TagClass.afterSleep;
    default:
      throw new Error(`invalid tag: ${tag}`);
  }
};

/**
 * Questionnaire types enum for self-assessments
 */
export enum QuestionnaireType {
  SleepApnea = 'sleepApnea',
  Insomnia = 'insomnia',
  Anxiety = 'anxiety',
  Depression = 'depression',
  Menopause = 'menopause',
}

/**
 * Map questionnaire type to i18n key
 * Returns the i18n key for translating questionnaire names
 */
export const getQuestionnaireI18nKey = (type: QuestionnaireType): string => {
  return `statistics:questionnaires.${type}`;
};
